import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ValidatedUrlOptionsParameter, ValidatedUrlOptionsParameterWithError } from '@bingads-webui-campaign-react/validated-url-input';
import { getUrlOptionsDetails } from '@bingads-webui-campaign-react/url-options';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { UrlOptionsImg } from '../../media';
import { SingleLineShimmer, withShimmer } from '../shimmers';

const getUrlOptionsStyles = () => ({
  urlOptionsContainer: {
    maxWidth: 435,
  },
});

const ValidatedUrlOptionsParameterWithShimmer = withShimmer(ValidatedUrlOptionsParameter, SingleLineShimmer);
const ValidatedUrlOptionsParameterWithErrorWithShimmer = withShimmer(ValidatedUrlOptionsParameterWithError, SingleLineShimmer);

export const UrlOptionsPanel = (props) => {
  const i18n = useContext(I18nContext);

  const classes = useDefaultStyles(getUrlOptionsStyles);

  const {
    key,
    isLoading,
    expand,
    options,
    data = {},
    isTrackingUrlTemplateRequired,
    isWithError,
    ...rest
  } = props;

  const {
    showDescription,
    urlParameterOptions,
  } = options;

  const {
    TrackingUrlTemplate: trackingUrlTemplate,
    FinalUrlSuffix: finalUrlSuffix,
    UrlCustomParameters: urlCustomParameters,
  } = data || {};

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate: trackingUrlTemplate,
    FinalUrlSuffix: finalUrlSuffix,
    UrlCustomParameters: urlCustomParameters,
  }), [trackingUrlTemplate, finalUrlSuffix, urlCustomParameters]);

  const urlOptionsDetails = useMemo(() => getUrlOptionsDetails(i18n, urlOptions), [urlOptions, i18n]);

  return (
    <ValidatedPanel
      key={key}
      title={
        <TextTitle
          titleKey={_TL_('Campaign URL options')}
          details={urlOptionsDetails}
          icon={UrlOptionsImg}
        />
      }
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      actuationProps={{
        expand,
        expandKey: key,
      }}
      {...rest}
    >
      <div className={classes.urlOptionsContainer}>
        {
          showDescription &&
          <span className="url-options-descriptions">
            {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
          </span>
        }
        {
          isWithError ?
            (<ValidatedUrlOptionsParameterWithErrorWithShimmer
              showShimmer={isLoading}
              i18n={i18n}
              validationProps={{
                initialValue: urlOptions,
                data: urlOptions,
              }}
              isTrackingUrlTemplateRequired={isTrackingUrlTemplateRequired}
              isWithError={isWithError}
              {...urlParameterOptions}
            />) :
            (<ValidatedUrlOptionsParameterWithShimmer
              showShimmer={isLoading}
              i18n={i18n}
              validationProps={{
                initialValue: urlOptions,
                data: urlOptions,
              }}
              {...urlParameterOptions}
            />)
        }
      </div>
    </ValidatedPanel>
  );
};

UrlOptionsPanel.propTypes = {
  key: PropTypes.string.isRequired,
  options: PropTypes.shape({
    showDescription: PropTypes.bool,
    urlParameterOptions: PropTypes.shape({}),
  }),
  data: PropTypes.shape({
    TrackingUrlTemplate: PropTypes.string,
    FinalUrlSuffix: PropTypes.string,
    UrlCustomParameters: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isLoading: PropTypes.bool,
  expand: PropTypes.func.isRequired,
  isTrackingUrlTemplateRequired: PropTypes.bool,
  isWithError: PropTypes.bool,
};

UrlOptionsPanel.defaultProps = {
  data: {},
  options: {},
  isLoading: false,
  isTrackingUrlTemplateRequired: false,
  isWithError: false,
};
